import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { OrderContext } from '../../context/OrderContext';
import { NotificationContext } from '../../context/NotificationContext';
import { customersAPI } from '../../api/customers';
import { servicesAPI } from '../../api/services';
import { ordersAPI } from '../../api/orders';
import { paymentsAPI } from '../../api/payments';
import Button from '../common/Button';
import Input from '../common/Input';
import Modal from '../common/Modal';
import { formatCurrency, formatPhone } from '../../utils/formatters';
import { validatePhone, validateEmail } from '../../utils/validators';
import { PAYMENT_METHODS } from '../../utils/constants';
import { subscriptionsAPI } from '../../api/subscriptions';
import SubscriptionBanner from './SubscriptionBanner';
import './POSInterface.css';
import './pos-subscription-styles.css';

const POSInterface = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchService, setSearchService] = useState('');
  const [searchCustomer, setSearchCustomer] = useState('');
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showCreateCustomer, setShowCreateCustomer] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showItemDetailsModal, setShowItemDetailsModal] = useState(false);
  const [showCustomItemModal, setShowCustomItemModal] = useState(false);
  const [selectedItemIndex, setSelectedItemIndex] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS.CASH);
  const [receivedAmount, setReceivedAmount] = useState('');
  const [expectedDelivery, setExpectedDelivery] = useState('');
  const [customerSubscription, setCustomerSubscription] = useState(null);
  const [useSubscription, setUseSubscription] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [completedOrder, setCompletedOrder] = useState(null);
  const [completedReceipt, setCompletedReceipt] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [loading, setLoading] = useState(false);

  // Wallet & Credit payment state
  const [useWallet, setUseWallet]         = useState(false);
  const [walletAmount, setWalletAmount]   = useState('');
  const [useCredit, setUseCredit]         = useState(false);
  const [customerCreditInfo, setCustomerCreditInfo] = useState(null);
  
  // Accordion state
  const [addonAccordionOpen, setAddonAccordionOpen] = useState(false);

  // Always-on inline numpad state
  const [numpadTarget, setNumpadTarget] = useState('discount'); // 'discount'|'addon'|'delivery'|'received'|'wallet'|'qty:{id}'
  const [numpadBuffer, setNumpadBuffer] = useState('');

  // Quick-add: type 8 digits on an empty cart → auto-find or register customer.
  const [quickPhoneBuffer, setQuickPhoneBuffer] = useState('');
  const [quickPhoneSearching, setQuickPhoneSearching] = useState(false);

  // Item details state
  const [itemDetails, setItemDetails] = useState({
    brand: '',
    type: '',
    colors: [],
    damages: [],
    stains: [],
    specialInstructions: '',
    attachments: []
  });

  // Custom item state
  const [customItem, setCustomItem] = useState({
    name: '',
    quantity: 1,
    price: 0,
    pieces: 1,
    category: 'dry_cleaning',
    taxExempt: false
  });

  // Damage/Stain form state
  const [damageForm, setDamageForm] = useState({
    type: '',
    description: '',
    location: ''
  });

  const [stainForm, setStainForm] = useState({
    type: '',
    severity: 'medium',
    location: ''
  });

  const [colorInput, setColorInput] = useState('');

  // New customer form state
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    phone: '',
    email: '',
    street: '',
    city: '',
    state: '',
    pincode: ''
  });

  const [customerErrors, setCustomerErrors] = useState({});

  const {
    currentOrder,
    addItem,
    updateItemQuantity,
    updateItemDetails,
    removeItem,
    setCustomer,
    setDiscount,
    setAddon,
    setDeliveryCharge,
    setDeliveryOption,
    setNotes,
    clearOrder,
    calculateTotal
  } = useContext(OrderContext);

  const { showNotification } = useContext(NotificationContext);

  useEffect(() => {
    loadCustomers();
    loadServices();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setExpectedDelivery(tomorrow.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (services.length > 0) {
      const categories = [...new Set(services.map(s => s.type))];
      setServiceCategories(['all', ...categories]);
    }
  }, [services]);

  // Load customer subscription when customer is selected
useEffect(() => {
  if (currentOrder.customer) {
    loadCustomerSubscription(currentOrder.customer._id);
    loadCustomerFinanceInfo(currentOrder.customer._id);
  } else {
    setCustomerSubscription(null);
    setUseSubscription(false);
    setCustomerCreditInfo(null);
    setUseWallet(false);
    setWalletAmount('');
    setUseCredit(false);
  }
}, [currentOrder.customer]);

  // Quick-add by phone: when the cart is empty and no customer is selected,
  // typing 8 digits anywhere on the keyboard searches for that customer and
  // either selects them or pops the registration dialog with the phone
  // pre-filled — no clicks needed.
  const quickPhoneEligible =
    !currentOrder.customer &&
    currentOrder.items.length === 0 &&
    !showCustomerModal &&
    !showCreateCustomer &&
    !showPaymentModal &&
    !showItemDetailsModal &&
    !showCustomItemModal &&
    !showSuccessModal;

  useEffect(() => {
    if (!quickPhoneEligible) {
      if (quickPhoneBuffer) setQuickPhoneBuffer('');
      return;
    }

    const onKeyDown = (e) => {
      // Don't hijack typing in actual form fields.
      const t = e.target;
      const tag = t?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        setQuickPhoneBuffer(prev => (prev + e.key).slice(0, 8));
        return;
      }
      if (e.key === 'Backspace') {
        e.preventDefault();
        setQuickPhoneBuffer(prev => prev.slice(0, -1));
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setQuickPhoneBuffer('');
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    quickPhoneEligible,
    quickPhoneBuffer,
  ]);

  // When the buffer reaches 8 digits, look the customer up and route them.
  useEffect(() => {
    if (quickPhoneBuffer.length !== 8 || !quickPhoneEligible) return;

    const phone = quickPhoneBuffer;
    setQuickPhoneSearching(true);
    (async () => {
      try {
        const res = await customersAPI.getAll({ search: phone, limit: 1, isActive: true });
        const match = (res.data || []).find(c => c.phone === phone);
        if (match) {
          setCustomer(match);
          setCustomers(prev => (prev.find(c => c._id === match._id) ? prev : [match, ...prev]));
          showNotification(`Customer ${match.name} selected`, 'success');
        } else {
          setNewCustomer(prev => ({ ...prev, phone }));
          setShowCreateCustomer(true);
          setShowCustomerModal(true);
          showNotification('No customer with this number — register them now', 'info');
        }
      } catch {
        showNotification('Lookup failed — try again', 'error');
      } finally {
        setQuickPhoneSearching(false);
        setQuickPhoneBuffer('');
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickPhoneBuffer]);

// Resolve the customer's active subscription (if any) for display in the POS.
// Auto-applies it when the customer still has a usable balance so the cashier
// doesn't have to remember to flip it on. Cashier can still untoggle it.
const loadCustomerSubscription = async (customerId) => {
  setLoadingSubscription(true);
  try {
    const response = await subscriptionsAPI.getActiveSubscription(customerId);
    const sub = Array.isArray(response.data) ? response.data[0] : response.data;
    setCustomerSubscription(sub || null);

    const hasUsableBalance = sub && (
      (sub.planType === 'credit' && (sub.creditRemaining || 0) > 0) ||
      (sub.planType === 'pieces' && (sub.piecesRemaining || 0) > 0)
    );
    setUseSubscription(!!hasUsableBalance);
  } catch {
    setCustomerSubscription(null);
    setUseSubscription(false);
  } finally {
    setLoadingSubscription(false);
  }
};

  // Load wallet + credit info when customer changes
  const loadCustomerFinanceInfo = async (customerId) => {
    try {
      const { walletAPI } = await import('../../api/wallet');
      const { creditAPI } = await import('../../api/credit');
      const [walletRes, creditRes] = await Promise.allSettled([
        walletAPI.getBalance(customerId),
        creditAPI.getSummary(customerId)
      ]);
      const wallet = walletRes.status === 'fulfilled' ? walletRes.value?.data : null;
      const credit = creditRes.status === 'fulfilled' ? creditRes.value?.data : null;
      setCustomerCreditInfo({ wallet, credit });
    } catch {
      setCustomerCreditInfo(null);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await customersAPI.getAll({ limit: 1000, isActive: true });
      setCustomers(response.data);
    } catch (error) {
      showNotification('Failed to load customers', 'error');
    }
  };

  const loadServices = async () => {
    try {
      const response = await servicesAPI.getAll({ isActive: true });
      setServices(response.data);
    } catch (error) {
      showNotification('Failed to load services', 'error');
    }
  };

  const filteredServices = services.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(searchService.toLowerCase());
    const matchesCategory = activeCategory === 'all' || s.type === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(searchCustomer.toLowerCase()) ||
    c.phone.includes(searchCustomer) ||
    (c.email && c.email.toLowerCase().includes(searchCustomer.toLowerCase()))
  );

  const { subtotal, total } = calculateTotal();

  const getCategoryLabel = (type) => {
    const labels = {
      'all': 'All Services',
      'dry_clean': 'Dry Cleaning',
      'wash': 'Laundry',
      'iron': 'Pressed / Ironed',
      'repairs': 'Repairs',
      'alterations': 'Alterations'
    };
    return labels[type] || type;
  };

  const getServiceIcon = (type) => {
    const icons = {
      'dry_cleaning': '🧥',
      'laundry': '👕',
      'pressed': '🔥',
      'repairs': '🪡',
      'alterations': '✂️',
      'wash': '💧',
      'iron': '♨️'
    };
    return icons[type] || '👔';
  };

  const handleServiceClick = (service) => {
    // Check if item already exists in order
    const existingItem = currentOrder.items.find(item => item.service._id === service._id);
    
    if (existingItem) {
      // Increase quantity if already exists
      updateItemQuantity(service._id, existingItem.quantity + 1);
      showNotification(`${service.name} quantity increased`, 'success');
    } else {
      // Add new item
      addItem(service, 1, {
        brand: '',
        type: '',
        colors: [],
        damages: [],
        stains: [],
        specialInstructions: '',
        attachments: []
      });
      showNotification(`${service.name} added`, 'success');
    }
  };

  const handleItemClick = (index) => {
    setSelectedItemIndex(index);
    const item = currentOrder.items[index];
    setItemDetails(item.itemDetails || {
      brand: '',
      type: '',
      colors: [],
      damages: [],
      stains: [],
      specialInstructions: '',
      attachments: []
    });
    setShowItemDetailsModal(true);
  };

  const handleAddColor = () => {
    if (colorInput.trim()) {
      setItemDetails(prev => ({
        ...prev,
        colors: [...(prev.colors || []), colorInput.trim()]
      }));
      setColorInput('');
    }
  };

  const handleRemoveColor = (index) => {
    setItemDetails(prev => ({
      ...prev,
      colors: prev.colors.filter((_, i) => i !== index)
    }));
  };

  const handleAddDamage = () => {
    if (damageForm.type.trim()) {
      setItemDetails(prev => ({
        ...prev,
        damages: [...(prev.damages || []), { ...damageForm }]
      }));
      setDamageForm({ type: '', description: '', location: '' });
    }
  };

  const handleRemoveDamage = (index) => {
    setItemDetails(prev => ({
      ...prev,
      damages: prev.damages.filter((_, i) => i !== index)
    }));
  };

  const handleAddStain = () => {
    if (stainForm.type.trim()) {
      setItemDetails(prev => ({
        ...prev,
        stains: [...(prev.stains || []), { ...stainForm }]
      }));
      setStainForm({ type: '', severity: 'medium', location: '' });
    }
  };

  const handleRemoveStain = (index) => {
    setItemDetails(prev => ({
      ...prev,
      stains: prev.stains.filter((_, i) => i !== index)
    }));
  };

  const handleSaveItemDetails = () => {
    if (selectedItemIndex !== null) {
      const item = currentOrder.items[selectedItemIndex];
      updateItemDetails(item.service._id, itemDetails);
      showNotification('Item details saved', 'success');
      setShowItemDetailsModal(false);
      setSelectedItemIndex(null);
    }
  };

  const handleCustomerSelect = (customer) => {
    setCustomer(customer);
    setShowCustomerModal(false);
    setSearchCustomer('');
    showNotification(`Customer ${customer.name} selected`, 'success');
  };

  const handleCustomerChange = (e) => {
    const { name, value } = e.target;
    setNewCustomer(prev => ({ ...prev, [name]: value }));
    if (customerErrors[name]) {
      setCustomerErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateCustomerForm = () => {
    const errors = {};
    if (!newCustomer.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!validatePhone(newCustomer.phone)) {
      errors.phone = 'Valid 10-digit phone number required';
    }
    if (newCustomer.email && !validateEmail(newCustomer.email)) {
      errors.email = 'Valid email is required';
    }
    setCustomerErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    if (!validateCustomerForm()) {
      return;
    }

    setLoading(true);
    try {
      const customerData = {
        name: newCustomer.name,
        phone: newCustomer.phone,
        email: newCustomer.email || undefined,
        address: {
          street: newCustomer.street,
          city: newCustomer.city,
          state: newCustomer.state,
          pincode: newCustomer.pincode
        }
      };

      const response = await customersAPI.create(customerData);
      showNotification('Customer created successfully!', 'success');
      setCustomers(prev => [response.data, ...prev]);
      setCustomer(response.data);
      setNewCustomer({
        name: '',
        phone: '',
        email: '',
        street: '',
        city: '',
        state: '',
        pincode: ''
      });
      setShowCreateCustomer(false);
      setShowCustomerModal(false);
    } catch (error) {
      showNotification(
        error.response?.data?.message || 'Failed to create customer',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomItem = () => {
    if (!customItem.name.trim() || customItem.price <= 0) {
      showNotification('Please enter valid item name and price', 'error');
      return;
    }

    const customService = {
      _id: `custom_${Date.now()}`,
      name: customItem.name,
      type: customItem.category,
      price: customItem.price,
      taxPercentage: customItem.taxExempt ? 0 : 5,
      isActive: true,
      isCustom: true
    };

    addItem(customService, customItem.quantity, {
      brand: '',
      type: customItem.name,
      colors: [],
      damages: [],
      stains: [],
      specialInstructions: '',
      attachments: []
    });

    showNotification('Custom item added', 'success');
    setShowCustomItemModal(false);
    setCustomItem({
      name: '',
      quantity: 1,
      price: 0,
      pieces: 1,
      category: 'dry_cleaning',
      taxExempt: false
    });
  };

  const handleProcessOrder = async () => {
    // Validation
  if (!currentOrder.customer) {
    showNotification('Please select a customer', 'error');
    return;
  }

  if (currentOrder.items.length === 0) {
    showNotification('Please add items to the order', 'error');
    return;
  }

  if (!expectedDelivery) {
    showNotification('Please select expected delivery date', 'error');
    return;
  }

  // If subscription is being used, skip payment modal and create order directly
  if (useSubscription && customerSubscription) {
    handleCreateOrderWithSubscription();
  } else {
    // Show normal payment modal (wallet/credit options available inside)
    setShowPaymentModal(true);
  }
  };

  const handleCreateOrderWithSubscription = async () => {
  setLoading(true);
  try {
    const orderData = {
      customer: currentOrder.customer._id,
      items: currentOrder.items.map(item => ({
        service: item.service._id,
        quantity: item.quantity,
        itemDetails: item.itemDetails
      })),
      addon: currentOrder.addon || 0,
      deliveryCharge: currentOrder.deliveryCharge || 0,
      discount: currentOrder.discount || 0,
      deliveryOption: currentOrder.deliveryOption,
      expectedDelivery,
      notes: currentOrder.notes,
      useSubscription: true,  // Subscription enabled
      paymentMethod: 'subscription_credit'
    };

    const response = await ordersAPI.create(orderData);
    
    // Show success modal with order and receipt data
    setCompletedOrder(response.data);
    setCompletedReceipt(response.receipt);
    setShowSuccessModal(true);
    setLoading(false);
  } catch (error) {
    setLoading(false);
    showNotification(
      error.response?.data?.message || 'Failed to create order',
      'error'
    );
  }
};

  const handlePayment = async () => {
  if (paymentMethod === PAYMENT_METHODS.CASH) {
    const changeAmount = parseFloat(receivedAmount) - total;
    if (changeAmount < 0) {
      showNotification('Received amount is less than total', 'error');
      return;
    }
  }

  setLoading(true);
  try {
    const orderData = {
      customer: currentOrder.customer._id,
      items: currentOrder.items.map(item => ({
        service: item.service._id,
        quantity: item.quantity,
        itemDetails: item.itemDetails
      })),
      addon: currentOrder.addon || 0,
      deliveryCharge: currentOrder.deliveryCharge || 0,
      discount: currentOrder.discount || 0,
      deliveryOption: currentOrder.deliveryOption,
      expectedDelivery,
      notes: currentOrder.notes,
      useSubscription: false,
      paymentMethod: useCredit ? 'on_credit' : paymentMethod,
      useWallet: useWallet && !useCredit,
      walletAmount: useWallet && !useCredit ? (parseFloat(walletAmount) || 0) : 0,
      useCredit
    };

    const response = await ordersAPI.create(orderData);
    
    // Close payment modal and show success modal
    setShowPaymentModal(false);
    setCompletedOrder(response.data);
    setCompletedReceipt(response.receipt);
    setShowSuccessModal(true);
    setLoading(false);
  } catch (error) {
    setLoading(false);
    showNotification(
      error.response?.data?.message || 'Failed to create order',
      'error'
    );
  }
};

const handleContinue = () => {
  // Close modal and reset form
  setShowSuccessModal(false);
  setCompletedOrder(null);
  setCompletedReceipt(null);
  clearOrder();
  setUseSubscription(false);
  setUseWallet(false);
  setWalletAmount('');
  setUseCredit(false);
  setReceivedAmount('');
  setPaymentMethod(PAYMENT_METHODS.CASH);
  showNotification('Ready for next order', 'success');
};

const handlePrintAndContinue = () => {
  // Open print dialog
  printReceipt(completedReceipt);
  
  // Then continue
  handleContinue();
};

const printReceipt = (receipt) => {
  if (!receipt) return;
  
  // Create printable content
  const printWindow = window.open('', '_blank');
  const printContent = generateReceiptHTML(receipt);
  
  printWindow.document.write(printContent);
  printWindow.document.close();
  printWindow.focus();
  
  // Auto print
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 250);
};

const generateReceiptHTML = (receipt) => {
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Receipt - ${receipt.orderNumber}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Courier New', monospace; width: 80mm; padding: 10mm; }
        .header { text-align: center; margin-bottom: 5mm; border-bottom: 2px dashed #000; padding-bottom: 5mm; }
        .company-name { font-size: 18px; font-weight: bold; }
        .order-number { font-size: 14px; margin: 3mm 0; }
        .section { margin: 5mm 0; }
        .section-title { font-weight: bold; border-bottom: 1px solid #000; margin-bottom: 2mm; }
        .row { display: flex; justify-content: space-between; margin: 2mm 0; }
        .items-table { width: 100%; border-collapse: collapse; }
        .items-table th, .items-table td { text-align: left; padding: 2mm 0; }
        .items-table th { border-bottom: 1px solid #000; }
        .total-section { border-top: 2px solid #000; margin-top: 5mm; padding-top: 3mm; }
        .total-row { display: flex; justify-content: space-between; font-size: 16px; font-weight: bold; }
        .footer { text-align: center; margin-top: 10mm; padding-top: 5mm; border-top: 2px dashed #000; font-size: 12px; }
        .subscription-badge { background: #f0f0f0; padding: 2mm; margin: 3mm 0; text-align: center; }
        @media print {
          body { width: 80mm; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="company-name">Your Laundry Service</div>
        <div>Kuwait City, Kuwait</div>
        <div>Tel: 9999 8888</div>
        <div class="order-number">Order: ${receipt.orderNumber}</div>
        <div>${new Date(receipt.orderDate).toLocaleString('en-KW')}</div>
      </div>
      
      <div class="section">
        <div class="section-title">CUSTOMER</div>
        <div>${receipt.customer.name}</div>
        <div>${receipt.customer.phone}</div>
      </div>
      
      <div class="section">
        <div class="section-title">ITEMS</div>
        <table class="items-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Qty</th>
              <th>Price</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            ${receipt.items.map(item => `
              <tr>
                <td>${item.name}</td>
                <td>${item.quantity}</td>
                <td>${(item.price).toFixed(3)}</td>
                <td>${(item.total).toFixed(3)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
      
      <div class="section">
        <div class="row"><span>Subtotal:</span><span>KD ${receipt.pricing.subtotal.toFixed(3)}</span></div>
        ${receipt.pricing.addon > 0 ? `<div class="row"><span>Addon:</span><span>KD ${receipt.pricing.addon.toFixed(3)}</span></div>` : ''}
        ${receipt.pricing.deliveryCharge > 0 ? `<div class="row"><span>Delivery:</span><span>KD ${receipt.pricing.deliveryCharge.toFixed(3)}</span></div>` : ''}
        ${receipt.pricing.discount > 0 ? `<div class="row"><span>Discount:</span><span>-KD ${receipt.pricing.discount.toFixed(3)}</span></div>` : ''}
        ${receipt.pricing.subscriptionDiscount > 0 ? `<div class="row"><span>Subscription Discount:</span><span>-KD ${receipt.pricing.subscriptionDiscount.toFixed(3)}</span></div>` : ''}
      </div>
      
      ${receipt.subscription ? `
        <div class="subscription-badge">
          ⭐ ${receipt.subscription.name}<br>
          Order on Credit
        </div>
      ` : ''}
      
      <div class="total-section">
        <div class="total-row">
          <span>TOTAL:</span>
          <span>KD ${receipt.pricing.totalAmount.toFixed(3)}</span>
        </div>
        ${receipt.pricing.paymentStatus === 'on_credit' ? `
          <div class="row" style="margin-top: 2mm;">
            <span>Payment Status:</span>
            <span>ON CREDIT</span>
          </div>
        ` : `
          <div class="row" style="margin-top: 2mm;">
            <span>Paid:</span>
            <span>KD ${receipt.pricing.finalAmount.toFixed(3)}</span>
          </div>
        `}
      </div>
      
      <div class="footer">
        Thank you for your business!<br>
        Expected Delivery: ${new Date(receipt.expectedDelivery).toLocaleDateString('en-KW')}
      </div>
    </body>
    </html>
  `;
};

  const changeAmount = receivedAmount ? parseFloat(receivedAmount) - total : 0;
  const today = new Date().toISOString().split('T')[0];

  // ── Always-on numpad helpers ──────────────────────────────────
  const activateField = (target) => {
    let val = '';
    switch (target) {
      case 'discount': val = String(currentOrder.discount || ''); break;
      case 'addon':    val = String(currentOrder.addon || ''); break;
      case 'delivery': val = String(currentOrder.deliveryCharge || ''); break;
      case 'received': val = receivedAmount || ''; break;
      case 'wallet':   val = walletAmount || ''; break;
      default:
        if (target?.startsWith('qty:')) {
          const sid = target.replace('qty:', '');
          const itm = currentOrder.items.find(i => i.service._id === sid);
          val = String(itm?.quantity || 1);
        }
    }
    setNumpadTarget(target);
    setNumpadBuffer(val === '0' ? '' : val);
  };

  const handleNumpadPress = (key) => {
    // Idle state — feed the on-screen numpad into the quick phone-search
    // buffer so the cashier can find or register a customer with no clicks.
    if (quickPhoneEligible) {
      if (key === 'C') {
        setQuickPhoneBuffer('');
        return;
      }
      if (key === '⌫') {
        setQuickPhoneBuffer(prev => prev.slice(0, -1));
        return;
      }
      if (/^[0-9]$/.test(key)) {
        setQuickPhoneBuffer(prev => (prev + key).slice(0, 8));
        return;
      }
      // Ignore '.' in idle state — phone numbers don't have decimals.
      if (key === '.') return;
    }

    const isQty = numpadTarget?.startsWith('qty:');
    let buf = numpadBuffer;

    if (key === 'C') {
      buf = '';
    } else if (key === '⌫') {
      buf = buf.slice(0, -1);
    } else if (key === '.') {
      if (!isQty && !buf.includes('.')) buf = (buf || '0') + '.';
    } else {
      if (buf === '' || buf === '0') buf = key;
      else buf = buf + key;
      if (buf.length > 10) return;
    }

    setNumpadBuffer(buf);
    const numVal = parseFloat(buf) || 0;

    switch (numpadTarget) {
      case 'discount': setDiscount(numVal); break;
      case 'addon':    setAddon(numVal); break;
      case 'delivery': setDeliveryCharge(numVal); break;
      case 'received': setReceivedAmount(buf); break;
      case 'wallet':   setWalletAmount(buf); break;
      default:
        if (numpadTarget?.startsWith('qty:')) {
          const sid = numpadTarget.replace('qty:', '');
          updateItemQuantity(sid, Math.max(1, parseInt(buf) || 1));
        }
    }
  };

  // When payment modal opens → target received amount in numpad
  useEffect(() => {
    if (showPaymentModal) activateField('received');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPaymentModal]);

  const damageTypes = ['Tear', 'Hole', 'Button Missing', 'Zipper Broken', 'Seam Open', 'Worn Out', 'Scratch'];
  const stainTypes = ['Oil', 'Ink', 'Wine', 'Food', 'Blood', 'Grease', 'Mud', 'Paint', 'Coffee', 'Makeup'];
  const colorPresets = ['Black', 'White', 'Blue', 'Red', 'Green', 'Yellow', 'Brown', 'Grey', 'Pink', 'Purple'];

  return (
    <div className="pos-screen">
      <div className="pos-container">

        {/* ── CATEGORY SIDEBAR ─────────────────────────────────── */}
        <div className="pos-categories-sidebar">
          {serviceCategories.map(category => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`cat-sidebar-btn ${activeCategory === category ? 'active' : ''}`}
            >
              {getCategoryLabel(category)}
            </button>
          ))}
        </div>

        {/* ── SERVICES PANEL ───────────────────────────────────── */}
        <div className="pos-services-panel">
          <div className="pos-search-bar">
            <Input
              type="text"
              placeholder="Search services..."
              value={searchService}
              onChange={(e) => setSearchService(e.target.value)}
            />
          </div>

          <div className="services-grid">
            {filteredServices.map(service => (
              <div
                key={service._id}
                className="service-card"
                onClick={() => handleServiceClick(service)}
              >
                <div className="service-icon-wrapper">
                  <span className="service-icon">{getServiceIcon(service.type)}</span>
                </div>
                <div className="service-info">
                  <div className="service-name">{service.name}</div>
                  <div className="service-price">{formatCurrency(service.price)}</div>
                </div>
              </div>
            ))}

            <div
              className="service-card custom-item-card"
              onClick={() => setShowCustomItemModal(true)}
            >
              <div className="service-icon-wrapper">
                <span className="service-icon">➕</span>
              </div>
              <div className="service-info">
                <div className="service-name">Custom Item</div>
                <div className="service-price">Add</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Order */}
        <div className="pos-right-panel">

          {/* ── CUSTOMER BAR ──────────────────────────────────── */}
          <div
            className={`rp-customer-bar${currentOrder.customer ? ' has-customer' : ''}${customerSubscription ? ' has-sub' : ''}`}
            onClick={() => !currentOrder.customer && setShowCustomerModal(true)}
          >
            {currentOrder.customer ? (
              <>
                <div className="rp-avatar">
                  {currentOrder.customer.name.charAt(0).toUpperCase()}
                </div>
                <div className="rp-cust-info">
                  <span className="rp-cust-name">{currentOrder.customer.name}</span>
                  <span className="rp-cust-phone">{formatPhone(currentOrder.customer.phone)}</span>
                  {loadingSubscription && <span className="rp-sub-loading">⏳ checking…</span>}
                  {customerSubscription && !loadingSubscription && (() => {
                    const sub = customerSubscription;
                    const isCredit = sub.planType === 'credit';
                    const balance = isCredit
                      ? formatCurrency(sub.creditRemaining || 0)
                      : `${sub.piecesRemaining || 0} pcs`;
                    const empty = isCredit
                      ? (sub.creditRemaining || 0) <= 0
                      : (sub.piecesRemaining || 0) <= 0;
                    const planName = sub.subscriptionPlan?.name || (isCredit ? 'Credit Plan' : 'Pieces Plan');
                    return (
                      <label
                        className="rp-sub-toggle"
                        onClick={e => e.stopPropagation()}
                        title={`${planName} — ${balance} available`}
                      >
                        <input
                          type="checkbox"
                          checked={useSubscription}
                          onChange={e => setUseSubscription(e.target.checked)}
                          disabled={empty}
                        />
                        <span className="rp-sub-slider" />
                        <span className="rp-sub-label">
                          ⭐ {empty ? 'No balance' : `${isCredit ? '🪙' : '👕'} ${balance}`}
                        </span>
                      </label>
                    );
                  })()}
                </div>

                {/* Delivery mode pills */}
                <div className="rp-delivery-pills" onClick={e => e.stopPropagation()}>
                  <button
                    className={`rp-pill${currentOrder.deliveryOption === 'standard' ? ' active' : ''}`}
                    onClick={() => setDeliveryOption('standard')}
                  >Standard</button>
                  <button
                    className={`rp-pill express${currentOrder.deliveryOption === 'express' ? ' active' : ''}`}
                    onClick={() => setDeliveryOption('express')}
                  >⚡ Express</button>
                </div>

                {/* Delivery date */}
                <div className="rp-date-inline" onClick={e => e.stopPropagation()}>
                  <span className="rp-date-inline-label">📅</span>
                  <input
                    type="date"
                    className="rp-date-input"
                    value={expectedDelivery}
                    onChange={e => setExpectedDelivery(e.target.value)}
                    min={today}
                  />
                </div>

                <button
                  className="rp-change-btn"
                  onClick={e => { e.stopPropagation(); setShowCustomerModal(true); }}
                >
                  Change
                </button>
              </>
            ) : (
              <div className="rp-no-customer">
                <span className="rp-no-customer-icon">👤</span>
                <div>
                  <div className="rp-no-customer-title">
                    {quickPhoneBuffer
                      ? (quickPhoneSearching ? 'Searching…' : 'Type phone number')
                      : 'No customer selected'}
                  </div>
                  <div className="rp-no-customer-hint">
                    {quickPhoneBuffer ? (
                      <span style={{ fontFamily: 'monospace', letterSpacing: 2, fontSize: 14 }}>
                        📞 {quickPhoneBuffer.padEnd(8, '_')}
                      </span>
                    ) : (
                      'Tap to select, or just type an 8-digit phone number'
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── ORDER ITEMS ───────────────────────────────────────── */}
          <div className="order-items-section">
            {currentOrder.items.length === 0 ? (
              <div className="empty-order">
                <div className="empty-order-icon">🧺</div>
                <p>No items added yet</p>
                <span className="empty-order-hint">Tap a service on the left to add</span>
              </div>
            ) : (
              <div className="order-items-table-wrapper">
                <table className="order-items-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th style={{ width: 44, textAlign: 'center' }}>Qty</th>
                      <th style={{ width: 74, textAlign: 'right' }}>Total</th>
                      <th style={{ width: 60, textAlign: 'center' }}>Act.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentOrder.items.map((item, index) => {
                      const itemTotal = item.service.price * item.quantity;
                      const hasDetails = item.itemDetails && (
                        item.itemDetails.brand ||
                        item.itemDetails.colors?.length > 0 ||
                        item.itemDetails.damages?.length > 0 ||
                        item.itemDetails.stains?.length > 0
                      );
                      return (
                        <tr key={index} className="order-item-row">
                          <td className="td-item" style={{width:'50%'}}>
                            <div className="item-name-cell">
                              <div className="item-title">{item.service.name}</div>
                              {hasDetails && (
                                <div className="item-details-tags">
                                  {item.itemDetails.brand && (
                                    <span className="detail-tag">🏷️ {item.itemDetails.brand}</span>
                                  )}
                                  {item.itemDetails.colors?.length > 0 && (
                                    <span className="detail-tag">🎨 {item.itemDetails.colors.join(', ')}</span>
                                  )}
                                  {item.itemDetails.damages?.length > 0 && (
                                    <span className="detail-tag warn">⚠️ {item.itemDetails.damages.length} dmg</span>
                                  )}
                                  {item.itemDetails.stains?.length > 0 && (
                                    <span className="detail-tag warn">💧 {item.itemDetails.stains.length} stain</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="td-qty-display">
                            <button
                              className={`qty-badge-btn${numpadTarget === `qty:${item.service._id}` ? ' active' : ''}`}
                              onClick={() => activateField(`qty:${item.service._id}`)}
                            >
                              ×{item.quantity}
                            </button>
                          </td>
                          <td className="td-total">{formatCurrency(itemTotal)}</td>
                          <td className="td-actions" style={{width:'20%', flexDirection:'row',display:'flex'}}>
                            <button onClick={() => handleItemClick(index)} className="btn-action-table" title="Details">📝</button>
                            <button onClick={() => removeItem(item.service._id)} className="btn-action-table btn-remove" title="Remove">🗑️</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── SUMMARY CHIPS + TOTAL ────────────────────────────── */}
          {(() => {
            const itemCount = currentOrder.items.reduce((sum, item) => sum + item.quantity, 0);
            const subPlanType = customerSubscription?.planType;
            const grossBeforeSub = Math.max(0, subtotal + (currentOrder.addon || 0) + (currentOrder.deliveryCharge || 0) - (currentOrder.discount || 0));

            const subscriptionCreditApplied =
              useSubscription && subPlanType === 'credit'
                ? Math.min(grossBeforeSub, customerSubscription.creditRemaining || 0)
                : 0;

            const subscriptionPiecesApplied =
              useSubscription && subPlanType === 'pieces'
                ? Math.min(itemCount, customerSubscription.piecesRemaining || 0)
                : 0;

            const isFreeExpress = false;

            const activeLabel = (() => {
              if (quickPhoneEligible) return '📞 Customer Phone';
              if (numpadTarget === 'discount') return '% Discount';
              if (numpadTarget === 'addon')    return '+ Addon';
              if (numpadTarget === 'delivery') return '🚚 Delivery';
              if (numpadTarget === 'received') return '💵 Amount Received';
              if (numpadTarget?.startsWith('qty:')) return '× Quantity';
              return 'Value';
            })();

            const numpadValue = quickPhoneEligible
              ? (quickPhoneBuffer ? quickPhoneBuffer.padEnd(8, '_') : '________')
              : (numpadBuffer !== '' ? numpadBuffer : '0');

            return (
              <>
                {/* Subscription badges */}
                {(subscriptionCreditApplied > 0 || subscriptionPiecesApplied > 0) && (
                  <div className="rp-sub-badges">
                    {subscriptionCreditApplied > 0 && (
                      <span className="rp-badge green">🪙 -{formatCurrency(subscriptionCreditApplied)} from credit</span>
                    )}
                    {subscriptionPiecesApplied > 0 && (
                      <span className="rp-badge purple">👕 {subscriptionPiecesApplied} piece{subscriptionPiecesApplied > 1 ? 's' : ''} covered</span>
                    )}
                  </div>
                )}

                {/* Summary row */}
                <div className="rp-summary-row">
                  <div className="rp-sum-cell static">
                    <span className="rp-sum-label">Sub</span>
                    <span className="rp-sum-val">{formatCurrency(subtotal)}</span>
                  </div>
                  <span className="rp-sum-op">+</span>
                  <button
                    className={`rp-sum-cell editable${numpadTarget === 'addon' ? ' active' : ''}`}
                    onClick={() => activateField('addon')}
                  >
                    <span className="rp-sum-label">Addon</span>
                    <span className="rp-sum-val">{currentOrder.addon > 0 ? currentOrder.addon.toFixed(3) : '—'}</span>
                  </button>
                  <span className="rp-sum-op">+</span>
                  <button
                    className={`rp-sum-cell editable${isFreeExpress ? ' free' : numpadTarget === 'delivery' ? ' active' : ''}`}
                    onClick={() => !isFreeExpress && activateField('delivery')}
                    disabled={isFreeExpress}
                  >
                    <span className="rp-sum-label">Delivery</span>
                    <span className="rp-sum-val">{isFreeExpress ? 'FREE' : currentOrder.deliveryCharge > 0 ? currentOrder.deliveryCharge.toFixed(3) : '—'}</span>
                  </button>
                  <span className="rp-sum-op">−</span>
                  <button
                    className={`rp-sum-cell editable disc${numpadTarget === 'discount' ? ' active' : ''}`}
                    onClick={() => activateField('discount')}
                  >
                    <span className="rp-sum-label">Disc</span>
                    <span className="rp-sum-val">{currentOrder.discount > 0 ? currentOrder.discount.toFixed(3) : '—'}</span>
                  </button>
                  <div className="rp-sum-cell total-chip">
                    <span className="rp-sum-label">Total</span>
                    <span className="rp-sum-val total-val">{formatCurrency(total)}</span>
                  </div>
                </div>

                {/* ── ALWAYS-ON NUMPAD BAR ────────────────────────────── */}
                <div className="rp-numpad-bar">
                  {/* Numpad display strip */}
                  <div className="rp-nb-display">
                    <span className="rp-nb-field">{activeLabel}</span>
                    <span
                      className="rp-nb-value"
                      style={quickPhoneEligible ? { fontFamily: 'monospace', letterSpacing: 2 } : undefined}
                    >
                      {numpadValue}
                    </span>
                  </div>

                  {/* 5-column grid: [left-action][d][d][d][right-action] × 4 rows */}
                  <div className="rp-nb-grid">
                    {/* Row 1 */}
                    <button className={`nb-fn${numpadTarget==='addon'?' nb-fn-active':''}`} onClick={() => activateField('addon')}>
                      <span className="nb-fn-icon">➕</span>
                      <span className="nb-fn-text">Addon</span>
                    </button>
                    <button className="nb-digit" onClick={() => handleNumpadPress('1')}>1</button>
                    <button className="nb-digit" onClick={() => handleNumpadPress('2')}>2</button>
                    <button className="nb-digit" onClick={() => handleNumpadPress('3')}>3</button>
                    <button className="nb-act nb-act-clear" onClick={clearOrder}>
                      <span className="nb-act-icon">🗑</span>
                      <span className="nb-act-text">Clear</span>
                    </button>

                    {/* Row 2 */}
                    <button className={`nb-fn${numpadTarget==='delivery'?' nb-fn-active':''}`} onClick={() => !isFreeExpress && activateField('delivery')} disabled={isFreeExpress}>
                      <span className="nb-fn-icon">🚚</span>
                      <span className="nb-fn-text">Delivery</span>
                    </button>
                    <button className="nb-digit" onClick={() => handleNumpadPress('4')}>4</button>
                    <button className="nb-digit" onClick={() => handleNumpadPress('5')}>5</button>
                    <button className="nb-digit" onClick={() => handleNumpadPress('6')}>6</button>
                    <button className="nb-act nb-act-back" onClick={() => handleNumpadPress('⌫')}>⌫</button>

                    {/* Row 3 */}
                    <button className={`nb-fn${numpadTarget==='discount'?' nb-fn-active':''}`} onClick={() => activateField('discount')}>
                      <span className="nb-fn-icon">%</span>
                      <span className="nb-fn-text">Discount</span>
                    </button>
                    <button className="nb-digit" onClick={() => handleNumpadPress('7')}>7</button>
                    <button className="nb-digit" onClick={() => handleNumpadPress('8')}>8</button>
                    <button className="nb-digit" onClick={() => handleNumpadPress('9')}>9</button>
                    <button className="nb-act nb-act-notes" onClick={() => setShowItemDetailsModal(currentOrder.items.length > 0)}>
                      <span className="nb-act-icon">📝</span>
                      <span className="nb-act-text">Notes</span>
                    </button>

                    {/* Row 4 */}
                    <button className="nb-fn" onClick={() => setShowCustomItemModal(true)}>
                      <span className="nb-fn-icon">✚</span>
                      <span className="nb-fn-text">Custom</span>
                    </button>
                    <button className="nb-digit nb-digit-dot" onClick={() => handleNumpadPress('.')}>.</button>
                    <button className="nb-digit" onClick={() => handleNumpadPress('0')}>0</button>
                    <button className="nb-digit nb-digit-c" onClick={() => handleNumpadPress('C')}>C</button>
                    <button
                      className="nb-act nb-act-pay"
                      onClick={handleProcessOrder}
                      disabled={currentOrder.items.length === 0 || !currentOrder.customer || loading}
                    >
                      <span className="nb-act-icon">💳</span>
                      <span className="nb-act-text">Pay</span>
                    </button>
                  </div>
                </div>
              </>
            );
          })()}

        </div>
      </div>

      {/* Customer Selection Modal */}
{/* Customer Selection Modal */}
<Modal
  isOpen={showCustomerModal}
  onClose={() => {
    setShowCustomerModal(false);
    setSearchCustomer('');
    setShowCreateCustomer(false);
  }}
  title={showCreateCustomer ? "Create New Customer" : "Select Customer"}
>
  {!showCreateCustomer ? (
    <>
      <div className="customer-modal-search">
        <Input
          type="text"
          placeholder="Search by name, phone, or email..."
          value={searchCustomer}
          onChange={(e) => setSearchCustomer(e.target.value)}
        />
        <Button onClick={() => setShowCreateCustomer(true)} variant="primary">
          + New Customer
        </Button>
      </div>

      <div className="customer-list-container">
        {filteredCustomers.length === 0 ? (
          <div className="no-customers-found">
            <p>No customers found</p>
            <span>Try a different search term or create a new customer</span>
          </div>
        ) : (
          <div className="customer-list">
            {filteredCustomers.map(customer => (
              <div
                key={customer._id}
                className="customer-list-item"
                onClick={() => handleCustomerSelect(customer)}
              >
                <div className="customer-list-avatar">
                  {customer.name.charAt(0).toUpperCase()}
                </div>
                <div className="customer-list-info">
                  <div className="customer-list-name">{customer.name}</div>
                  <div className="customer-list-contact">
                    <span className="contact-item">
                      📞 {formatPhone(customer.phone)}
                    </span>
                    {customer.email && (
                      <span className="contact-item">
                        ✉️ {customer.email}
                      </span>
                    )}
                  </div>
                  {customer.address && (customer.address.city || customer.address.street) && (
                    <div className="customer-list-address">
                      📍 {[customer.address.street, customer.address.city].filter(Boolean).join(', ')}
                    </div>
                  )}
                </div>
                <div className="customer-list-arrow">›</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  ) : (
    <div className="customer-form-container">
      <button className="btn-back" onClick={() => setShowCreateCustomer(false)}>
        ← Back to List
      </button>

      <form onSubmit={handleCreateCustomer} className="customer-form">
        <div>
          <Input
            label="Name *"
            type="text"
            name="name"
            value={newCustomer.name}
            onChange={handleCustomerChange}
            error={customerErrors.name}
          />
        </div>

        <div className="form-grid-2col">
          <Input
            label="Phone *"
            type="tel"
            name="phone"
            value={newCustomer.phone}
            onChange={handleCustomerChange}
            error={customerErrors.phone}
          />
          <Input
            label="Email"
            type="email"
            name="email"
            value={newCustomer.email}
            onChange={handleCustomerChange}
            error={customerErrors.email}
          />
        </div>

        <div>
          <Input
            label="Street Address"
            type="text"
            name="street"
            value={newCustomer.street}
            onChange={handleCustomerChange}
          />
        </div>

        <div className="form-grid-2col">
          <Input
            label="City"
            type="text"
            name="city"
            value={newCustomer.city}
            onChange={handleCustomerChange}
          />
          <Input
            label="State"
            type="text"
            name="state"
            value={newCustomer.state}
            onChange={handleCustomerChange}
          />
        </div>

        <div>
          <Input
            label="Pincode"
            type="text"
            name="pincode"
            value={newCustomer.pincode}
            onChange={handleCustomerChange}
          />
        </div>

        <div className="form-actions">
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowCreateCustomer(false)}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Creating...' : 'Create Customer'}
          </Button>
        </div>
      </form>
    </div>
  )}
</Modal>


      {/* Item Details Modal */}
      <Modal
        isOpen={showItemDetailsModal}
        onClose={() => {
          setShowItemDetailsModal(false);
          setSelectedItemIndex(null);
        }}
        title="Item Details"
      >
        <div className="item-details-form">
          <div className="details-section">
            <label className="section-label">Brand</label>
            <Input
              type="text"
              placeholder="Enter brand name"
              value={itemDetails.brand}
              onChange={(e) => setItemDetails(prev => ({ ...prev, brand: e.target.value }))}
            />
          </div>

          <div className="details-section">
            <label className="section-label">Colors</label>
            <div className="color-preset-grid">
              {colorPresets.map(color => (
                <button
                  key={color}
                  type="button"
                  className={`color-preset-btn ${itemDetails.colors?.includes(color) ? 'selected' : ''}`}
                  onClick={() => {
                    if (itemDetails.colors?.includes(color)) {
                      setItemDetails(prev => ({
                        ...prev,
                        colors: prev.colors.filter(c => c !== color)
                      }));
                    } else {
                      setItemDetails(prev => ({
                        ...prev,
                        colors: [...(prev.colors || []), color]
                      }));
                    }
                  }}
                >
                  {color}
                </button>
              ))}
            </div>

            <div className="add-custom-group">
              <Input
                type="text"
                placeholder="Custom color"
                value={colorInput}
                onChange={(e) => setColorInput(e.target.value)}
              />
              <Button onClick={handleAddColor}>Add</Button>
            </div>

            {itemDetails.colors?.length > 0 && (
              <div className="tags-list">
                {itemDetails.colors.map((color, index) => (
                  <span key={index} className="tag">
                    {color}
                    <button onClick={() => handleRemoveColor(index)}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="details-section">
            <label className="section-label">Damages</label>
            <div className="damage-stain-grid">
              <select
                value={damageForm.type}
                onChange={(e) => setDamageForm(prev => ({ ...prev, type: e.target.value }))}
                className="mini-input"
              >
                <option value="">Select type...</option>
                {damageTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <Input
                type="text"
                placeholder="Location"
                value={damageForm.location}
                onChange={(e) => setDamageForm(prev => ({ ...prev, location: e.target.value }))}
              />
              <Input
                type="text"
                placeholder="Description"
                value={damageForm.description}
                onChange={(e) => setDamageForm(prev => ({ ...prev, description: e.target.value }))}
              />
              <Button onClick={handleAddDamage}>Add</Button>
            </div>

            {itemDetails.damages?.length > 0 && (
              <div className="details-list">
                {itemDetails.damages.map((damage, index) => (
                  <div key={index} className="detail-item">
                    <div>
                      <strong>{damage.type}</strong> - {damage.location}
                      {damage.description && (
                        <div className="detail-desc">{damage.description}</div>
                      )}
                    </div>
                    <button
                      className="btn-remove-detail"
                      onClick={() => handleRemoveDamage(index)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="details-section">
            <label className="section-label">Stains</label>
            <div className="damage-stain-grid">
              <select
                value={stainForm.type}
                onChange={(e) => setStainForm(prev => ({ ...prev, type: e.target.value }))}
                className="mini-input"
              >
                <option value="">Select type...</option>
                {stainTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <select
                value={stainForm.severity}
                onChange={(e) => setStainForm(prev => ({ ...prev, severity: e.target.value }))}
                className="mini-input"
              >
                <option value="light">Light</option>
                <option value="medium">Medium</option>
                <option value="heavy">Heavy</option>
              </select>
              <Input
                type="text"
                placeholder="Location"
                value={stainForm.location}
                onChange={(e) => setStainForm(prev => ({ ...prev, location: e.target.value }))}
              />
              <Button onClick={handleAddStain}>Add</Button>
            </div>

            {itemDetails.stains?.length > 0 && (
              <div className="details-list">
                {itemDetails.stains.map((stain, index) => (
                  <div key={index} className="detail-item">
                    <div>
                      <strong>{stain.type}</strong> - {stain.location}
                      <span className={`severity-badge ${stain.severity}`}>
                        {stain.severity}
                      </span>
                    </div>
                    <button
                      className="btn-remove-detail"
                      onClick={() => handleRemoveStain(index)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="details-section">
            <label className="section-label">Special Instructions</label>
            <textarea
              className="textarea-input"
              placeholder="Add any special instructions..."
              value={itemDetails.specialInstructions}
              onChange={(e) => setItemDetails(prev => ({
                ...prev,
                specialInstructions: e.target.value
              }))}
              rows="4"
              maxLength="500"
            />
            <div className="char-count">
              {itemDetails.specialInstructions?.length || 0}/500
            </div>
          </div>

          <div className="form-actions">
            <Button
              variant="outline"
              onClick={() => {
                setShowItemDetailsModal(false);
                setSelectedItemIndex(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveItemDetails}>
              Save Details
            </Button>
          </div>
        </div>
      </Modal>

      {/* Custom Item Modal */}
      <Modal
        isOpen={showCustomItemModal}
        onClose={() => setShowCustomItemModal(false)}
        title="Add Custom Item"
      >
        <div className="custom-item-form">
          <Input
            label="Item Name"
            type="text"
            placeholder="Enter item name"
            value={customItem.name}
            onChange={(e) => setCustomItem(prev => ({ ...prev, name: e.target.value }))}
          />

          <div className="form-grid-2col">
            <Input
              label="Price"
              type="number"
              placeholder="0.00"
              value={customItem.price || ''}
              onChange={(e) => setCustomItem(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
            />
            <Input
              label="Quantity"
              type="number"
              placeholder="1"
              min="1"
              value={customItem.quantity}
              onChange={(e) => setCustomItem(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
            />
          </div>

          <div>
            <label className="section-label">Category</label>
            <select
              className="mini-input"
              style={{ width: '100%' }}
              value={customItem.category}
              onChange={(e) => setCustomItem(prev => ({ ...prev, category: e.target.value }))}
            >
              <option value="dry_cleaning">Dry Cleaning</option>
              <option value="laundry">Laundry</option>
              <option value="pressed">Pressed/Ironed</option>
              <option value="repairs">Repairs</option>
              <option value="alterations">Alterations</option>
            </select>
          </div>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={customItem.taxExempt}
              onChange={(e) => setCustomItem(prev => ({ ...prev, taxExempt: e.target.checked }))}
            />
            Tax Exempt
          </label>

          <div className="form-actions">
            <Button variant="outline" onClick={() => setShowCustomItemModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddCustomItem}>
              Add Item
            </Button>
          </div>
        </div>
      </Modal>

      {/* Payment Modal - with Wallet & Credit */}
      <Modal
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          setUseWallet(false);
          setWalletAmount('');
          setUseCredit(false);
        }}
        title="Process Payment"
      >
        <div className="payment-form">
          {/* Order total */}
          <div className="payment-summary-card">
            <h3>Order Total</h3>
            <div className="amount-display">{formatCurrency(total)}</div>
          </div>

          {/* ── Wallet Option ─────────────────────────────────────────── */}
          {customerCreditInfo?.wallet && customerCreditInfo.wallet.walletBalance > 0 && !useCredit && (
            <div className="payment-wallet-section">
              <div className="wallet-toggle-row">
                <label className="wallet-toggle-label">
                  <input
                    type="checkbox"
                    checked={useWallet}
                    onChange={e => {
                      setUseWallet(e.target.checked);
                      if (!e.target.checked) setWalletAmount('');
                    }}
                  />
                  <span className="wallet-toggle-text">
                    💰 Use Wallet Balance
                    <span className="wallet-balance-chip">
                      Available: {formatCurrency(customerCreditInfo.wallet.walletBalance)}
                    </span>
                  </span>
                </label>
              </div>
              {useWallet && (
                <div className="wallet-amount-row">
                  <div className="payment-received-row">
                    <label className="section-label">Wallet Amount to Use (KD)</label>
                    <div className="payment-received-display wallet">
                      <span className="payment-received-value">{walletAmount || '0'}</span>
                      <button className="wallet-numpad-switch-btn" onClick={() => activateField('wallet')}>
                        Edit
                      </button>
                    </div>
                  </div>
                  <button
                    className="wallet-max-btn"
                    type="button"
                    onClick={() => setWalletAmount(
                      Math.min(customerCreditInfo.wallet.walletBalance, total).toFixed(3)
                    )}
                  >
                    Use Max
                  </button>
                  {walletAmount > 0 && (
                    <div className="wallet-remaining-display">
                      Remaining to pay: <strong>
                        {formatCurrency(Math.max(0, total - parseFloat(walletAmount || 0)))}
                      </strong>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Credit Option ──────────────────────────────────────────── */}
          {customerCreditInfo?.credit && customerCreditInfo.credit.creditLimit > 0 && (
            <div className="payment-credit-section">
              <div className="credit-toggle-row">
                <label className="credit-toggle-label">
                  <input
                    type="checkbox"
                    checked={useCredit}
                    onChange={e => {
                      setUseCredit(e.target.checked);
                      if (e.target.checked) {
                        setUseWallet(false);
                        setWalletAmount('');
                      }
                    }}
                    disabled={customerCreditInfo.credit.availableCredit < total}
                  />
                  <span className="credit-toggle-text">
                    🏦 Place on Credit
                    <span className={`credit-avail-chip ${customerCreditInfo.credit.availableCredit < total ? 'insufficient' : ''}`}>
                      Available: {formatCurrency(customerCreditInfo.credit.availableCredit)}
                    </span>
                  </span>
                </label>
                {customerCreditInfo.credit.availableCredit < total && (
                  <span className="credit-insufficient-note">
                    Insufficient credit limit
                  </span>
                )}
              </div>
              {useCredit && (
                <div className="credit-notice">
                  ℹ️ Full amount of {formatCurrency(total)} will be added to customer's credit account.
                  No payment collected now.
                </div>
              )}
            </div>
          )}

          {/* ── Standard Payment Method (only when not using full credit) ── */}
          {!useCredit && (
            <>
              {/* Show payment method only if wallet doesn't cover full amount */}
              {(!useWallet || parseFloat(walletAmount || 0) < total) && (
                <div>
                  <label className="section-label">
                    {useWallet && parseFloat(walletAmount || 0) > 0
                      ? `Payment Method for remaining ${formatCurrency(Math.max(0, total - parseFloat(walletAmount || 0)))}`
                      : 'Payment Method'
                    }
                  </label>
                  <div className="payment-methods-grid">
                    {Object.values(PAYMENT_METHODS).map(method => (
                      <button
                        key={method}
                        className={`payment-method-btn ${paymentMethod === method ? 'active' : ''}`}
                        onClick={() => setPaymentMethod(method)}
                      >
                        {method.replace('_', ' ').toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {paymentMethod === PAYMENT_METHODS.CASH &&
                (!useWallet || parseFloat(walletAmount || 0) < total) && (
                <>
                  <div className="payment-received-row">
                    <label className="section-label">Received Amount</label>
                    <div className={`payment-received-display${changeAmount < 0 ? ' insufficient' : changeAmount >= 0 && receivedAmount ? ' sufficient' : ''}`}>
                      <span className="payment-received-value">{receivedAmount || '0'}</span>
                      <span className="payment-received-hint">← use panel numpad</span>
                    </div>
                  </div>
                  {receivedAmount && (
                    <div className={`change-display ${changeAmount < 0 ? 'insufficient' : ''}`}>
                      <span className="change-label">Change:</span>
                      <span className="change-value">{formatCurrency(Math.abs(changeAmount))}</span>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* ── Inline Numpad (modal) ──────────────────────────────── */}

          <div className="form-actions">
            <Button variant="outline" onClick={() => {
              setShowPaymentModal(false);
              setUseWallet(false);
              setWalletAmount('');
              setUseCredit(false);
            }}>
              Cancel
            </Button>
            <Button onClick={handlePayment} disabled={loading}>
              {loading ? 'Processing...' : useCredit ? '📋 Place on Credit' : '✓ Complete Payment'}
            </Button>
          </div>
        </div>
      </Modal>
      {/* Success Modal - NEW */}
<Modal
  isOpen={showSuccessModal}
  onClose={() => {}}  // Prevent closing by clicking outside
  title="Order Created Successfully!"
>
  <div className="success-modal-content">
    <div className="success-icon-large">✓</div>
    
    <div className="success-details">
      <div className="success-detail-row">
        <span className="detail-label">Order Number:</span>
        <span className="detail-value order-number">{completedOrder?.orderNumber}</span>
      </div>
      
      <div className="success-detail-row">
        <span className="detail-label">Customer:</span>
        <span className="detail-value">{completedOrder?.customer?.name}</span>
      </div>
      
      <div className="success-detail-row">
        <span className="detail-label">Total Amount:</span>
        <span className="detail-value total-amount">
          {formatCurrency(completedOrder?.totalAmount || 0)}
        </span>
      </div>
      
      {completedOrder?.useSubscription && (
        <>
          <div className="success-detail-row highlight">
            <span className="detail-label">Subscription Discount:</span>
            <span className="detail-value discount">
              -{formatCurrency(completedOrder?.subscriptionDiscount || 0)}
            </span>
          </div>
          
          <div className="subscription-credit-badge">
            ⭐ Order on Credit (Subscription)
          </div>
        </>
      )}
      
      <div className={`success-detail-row final ${completedOrder?.useSubscription ? 'subscription-order' : ''}`}>
  <span className="detail-label">
    {completedOrder?.useSubscription ? 'Payment Status:' : 'Amount Paid:'}
  </span>
  <span className="detail-value final-amount">
    {completedOrder?.useSubscription ? (
      <>
        <span>KD 0.000</span>
        <span className="credit-note">Processed on Credit</span>
      </>
    ) : (
      formatCurrency(completedOrder?.finalAmount || 0)
    )}
  </span>
</div>
    </div>
    
    <div className="success-modal-actions">
      <Button
        variant="secondary"
        size="medium"
        onClick={handleContinue}
      >
        Continue
      </Button>
      <Button
        variant="primary"
        size="medium"
        onClick={handlePrintAndContinue}
      >
        🖨️ Print & Continue
      </Button>
    </div>
  </div>
</Modal>
    </div>
  );
};

export default POSInterface;